import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  Clock3,
  ExternalLink,
  Loader2,
  MailCheck,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { persistEmailVerificationResume } from '../utils/emailVerification';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

interface VerifyEmailPageProps {
  isDarkMode: boolean;
}

interface ConfirmationResponse {
  success?: boolean;
  status?: 'verified';
  challengeId?: string;
  customerSlug?: string;
  email?: string;
  expiresAt?: string;
  message?: string;
  error?: string;
}

type ConfirmationState = 'idle' | 'loading' | 'success' | 'error';

const VerifyEmailPage: React.FC<VerifyEmailPageProps> = ({ isDarkMode }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() || '';
  const requestedReturnTo = searchParams.get('returnTo')?.trim() || '';
  const isAvatarShare = searchParams.get('purpose') === 'avatar-share'
    && /^\/share\/[A-Za-z0-9_-]{20,200}$/.test(requestedReturnTo);
  const continuePath = isAvatarShare ? requestedReturnTo : '/setup';
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>(token ? 'idle' : 'error');
  const [errorMessage, setErrorMessage] = useState(
    token ? '' : 'Der Bestätigungslink ist unvollständig. Bitte fordere einen neuen Link an.',
  );
  const [customerSlug, setCustomerSlug] = useState('');

  const confirmEmail = async () => {
    if (!token || confirmationState === 'loading' || confirmationState === 'success') {
      return;
    }

    setConfirmationState('loading');
    setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE}/api/customer/workspace/setup/email-verification/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const payload = (await response.json().catch(() => null)) as ConfirmationResponse | null;
      if (!response.ok || !payload?.success || payload.status !== 'verified') {
        throw new Error(
          payload?.error || payload?.message || 'Die E-Mail-Adresse konnte nicht bestätigt werden. Der Link ist möglicherweise abgelaufen.',
        );
      }

      const nextCustomerSlug = payload.customerSlug || '';
      if (payload.challengeId && payload.email && nextCustomerSlug) {
        persistEmailVerificationResume({
          challengeId: payload.challengeId,
          email: payload.email,
          customerSlug: nextCustomerSlug,
          expiresAt: payload.expiresAt || '',
          status: 'verified',
        });
      }
      setCustomerSlug(nextCustomerSlug);
      setConfirmationState('success');
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : 'Die E-Mail-Adresse konnte nicht bestätigt werden. Bitte versuche es erneut.',
      );
      setConfirmationState('error');
    }
  };

  const closeWindow = () => {
    window.close();
  };

  const pageClasses = isDarkMode
    ? 'bg-slate-950 text-slate-50'
    : 'bg-slate-100 text-slate-950';
  const cardClasses = isDarkMode
    ? 'border-white/10 bg-slate-900/95 shadow-[0_32px_80px_rgba(2,6,23,0.65)]'
    : 'border-slate-200 bg-white shadow-[0_32px_80px_rgba(15,23,42,0.14)]';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  return (
    <main className={`min-h-screen px-4 py-10 sm:px-6 ${pageClasses}`}>
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-2xl items-center justify-center">
        <section className={`relative w-full overflow-hidden rounded-[32px] border p-7 sm:p-10 ${cardClasses}`} aria-labelledby="verification-heading">
          <div className={`absolute inset-x-0 top-0 h-1 ${confirmationState === 'success' ? 'bg-emerald-500' : 'bg-gradient-to-r from-slate-900 via-blue-600 to-blue-400'}`} aria-hidden="true" />

          <div className="flex items-center justify-between gap-4">
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Synaworks · {isAvatarShare ? 'Sicherer Avatar-Zugang' : 'Sicheres Setup'}
            </p>
            <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
              confirmationState === 'success'
                ? isDarkMode ? 'bg-emerald-400/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                : isDarkMode ? 'bg-blue-400/15 text-blue-200' : 'bg-blue-50 text-blue-700'
            }`}>
              {confirmationState === 'success'
                ? <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
                : <ShieldCheck className="h-6 w-6" aria-hidden="true" />}
            </div>
          </div>

          <div className="mt-8" aria-live="polite">
            {confirmationState === 'success' ? (
              <>
                <h1 id="verification-heading" className="max-w-lg text-3xl font-semibold tracking-tight sm:text-4xl">
                  E-Mail-Adresse bestätigt
                </h1>
                <p className={`mt-4 max-w-xl text-base leading-7 ${mutedClasses}`}>
                  {isAvatarShare
                    ? 'Alles erledigt. Kehre jetzt zur Einladungsseite zurück. Eine dort bereits geöffnete Registrierung erkennt die Bestätigung automatisch.'
                    : 'Alles erledigt. Du kannst die Registrierung auf diesem Gerät fortsetzen. Ein bereits geöffnetes Setup erkennt die Bestätigung ebenfalls automatisch.'}
                </p>
                {customerSlug ? (
                  <div className={`mt-6 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${isDarkMode ? 'border-slate-700 bg-slate-950/50 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                    <span className={mutedClasses}>{isAvatarShare ? 'Kunde' : 'Customer-ID'}</span>
                    <strong>{customerSlug}</strong>
                  </div>
                ) : null}

                <div className={`mt-8 rounded-2xl border px-5 py-4 ${isDarkMode ? 'border-emerald-400/20 bg-emerald-400/10' : 'border-emerald-200 bg-emerald-50'}`}>
                  <div className="flex gap-3">
                    <MailCheck className={`mt-0.5 h-5 w-5 shrink-0 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`} aria-hidden="true" />
                    <p className={`text-sm leading-6 ${isDarkMode ? 'text-emerald-100' : 'text-emerald-900'}`}>
                      {isAvatarShare
                        ? 'Wurde die Registrierung auf einem anderen Gerät begonnen, öffnet der folgende Button dort erneut die geschützte Avatar-Einladung. Benutzername und Passwort gibst du aus Sicherheitsgründen erneut ein.'
                        : 'Wenn die Registrierung auf einem anderen Gerät begonnen wurde, kannst du sie hier fortsetzen. Nicht übertragene Angaben und das Passwort gibst du dort erneut ein.'}
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={closeWindow}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.2)] transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Fenster schließen
                  </button>
                  <Link
                    to={continuePath}
                    className={`inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border px-5 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isDarkMode ? 'border-slate-700 text-slate-100 hover:bg-slate-800 focus:ring-offset-slate-900' : 'border-slate-200 text-slate-800 hover:bg-slate-50'}`}
                  >
                    {isAvatarShare ? 'Avatar-Registrierung fortsetzen' : 'Registrierung fortsetzen'}
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h1 id="verification-heading" className="max-w-lg text-3xl font-semibold tracking-tight sm:text-4xl">
                  E-Mail-Adresse bestätigen
                </h1>
                <p className={`mt-4 max-w-xl text-base leading-7 ${mutedClasses}`}>
                  {isAvatarShare
                    ? 'Bestätige, dass diese E-Mail-Adresse dir gehört. Erst danach wird dein persönlicher Zugang zu diesem Avatar erstellt.'
                    : 'Bestätige, dass diese E-Mail-Adresse dir gehört. Erst danach darf das Synaworks-Setup den neuen Kundenbereich anlegen.'}
                </p>

                <div className={`mt-8 grid grid-cols-3 border-y py-5 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`} aria-label="Ablauf der Registrierung">
                  {[
                    { label: 'Adresse erfasst', complete: true },
                    { label: 'Inbox bestätigen', complete: false },
                    { label: isAvatarShare ? 'Zugang startet' : 'Setup startet', complete: false },
                  ].map((stage, index) => (
                    <div key={stage.label} className="relative flex flex-col items-center gap-2 px-1 text-center">
                      {index < 2 ? (
                        <span className={`absolute left-[calc(50%+17px)] right-[calc(-50%+17px)] top-[15px] h-px ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`} aria-hidden="true" />
                      ) : null}
                      <span className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border ${
                        stage.complete
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : index === 1
                            ? isDarkMode ? 'border-blue-300 bg-blue-400/15 text-blue-100' : 'border-blue-600 bg-white text-blue-700'
                            : isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-500' : 'border-slate-200 bg-white text-slate-400'
                      }`}>
                        {stage.complete
                          ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                          : index === 1
                            ? <MailCheck className="h-4 w-4" aria-hidden="true" />
                            : <span className="text-xs font-bold">3</span>}
                      </span>
                      <span className={`text-[11px] font-medium leading-4 ${index <= 1 ? (isDarkMode ? 'text-slate-200' : 'text-slate-700') : mutedClasses}`}>
                        {stage.label}
                      </span>
                    </div>
                  ))}
                </div>

                {confirmationState === 'error' && errorMessage ? (
                  <div className={`mt-6 flex gap-3 rounded-2xl border px-4 py-4 ${isDarkMode ? 'border-red-400/25 bg-red-400/10 text-red-100' : 'border-red-200 bg-red-50 text-red-800'}`} role="alert">
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                    <p className="text-sm leading-6">{errorMessage}</p>
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => void confirmEmail()}
                  disabled={!token || confirmationState === 'loading'}
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-slate-950 to-blue-700 px-6 py-3.5 text-sm font-semibold text-white shadow-[0_16px_36px_rgba(37,99,235,0.24)] transition hover:scale-[1.01] hover:shadow-[0_20px_38px_rgba(15,23,42,0.25)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100 sm:w-auto sm:min-w-[280px]"
                >
                  {confirmationState === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Adresse wird bestätigt
                    </>
                  ) : (
                    <>
                      <MailCheck className="h-4 w-4" aria-hidden="true" />
                      E-Mail-Adresse bestätigen
                    </>
                  )}
                </button>

                <div className={`mt-6 flex gap-2 text-xs leading-5 ${mutedClasses}`}>
                  <Clock3 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <p>Der Link ist zeitlich begrenzt und bestätigt nur diese Registrierung. Die Bestätigung erfolgt erst nach deinem Klick.</p>
                </div>

                <p className={`mt-8 text-sm ${mutedClasses}`}>
                  Link abgelaufen oder falsche Adresse?{' '}
                  <Link to="/setup" className={`font-semibold underline underline-offset-4 ${isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-700 hover:text-blue-900'}`}>
                    Zurück zum Setup
                  </Link>
                </p>
              </>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default VerifyEmailPage;
